import {Select, SelectProps, Spin} from "antd";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {debounce} from "underscore";

export interface DebounceSelectProps<ValueType = any>
    extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
    fetchOptions: (search: string) => Promise<ValueType[]>;
    debounceTimeout?: number;
    allowSearchAsOption?: boolean;
    initOptions?: ValueType[];
}

const DebounceSelect = <ValueType extends { key?: string; label: React.ReactNode; value: string | number } = any, >
    ({ allowSearchAsOption, fetchOptions, debounceTimeout, initOptions, ...props }: DebounceSelectProps<ValueType>) => {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<ValueType[]>(initOptions ? [...initOptions] : []);
    const fetchRef = useRef(0);

    useEffect(() => {
        setOptions(initOptions ? [...initOptions] : []);
    }, [initOptions]);

    const debounceFetcher = useMemo(() => {
        const loadOptions = (searchTerms: string) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);

            fetchOptions(searchTerms).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    // for fetch callback order
                    return;
                }

                newOptions.sort((optionA, optionB) =>
                    (optionA ? `${optionA.label}` : '').toLowerCase().localeCompare((optionB ? `${optionB.label}` : '').toLowerCase())
                );

                if(!!allowSearchAsOption){
                    newOptions.unshift({label: searchTerms, value: searchTerms} as ValueType);
                }

                setOptions(newOptions);
                setFetching(false);
            });
        };

        return debounce(loadOptions, debounceTimeout ? debounceTimeout : 800);
    }, [fetchOptions, debounceTimeout, allowSearchAsOption]);

    return (
        <Select
            className={'width-200px'}
            placeholder="Search to select..."
            showSearch
            onSearch={debounceFetcher}
            labelInValue
            filterOption={false}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            {...props}
            options={options}
        />
    );
}

export default DebounceSelect;